import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, className, id, ...rest },
  ref
) {
  const inputId = id ?? rest.name;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-medium text-ink">
          {label}
        </label>
      )}
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border bg-surface px-3 shadow-subtle transition-[border,box-shadow]',
          'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20',
          error ? 'border-danger' : 'border-border'
        )}
      >
        {leading && <span className="text-ink-mute">{leading}</span>}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'peer h-10 w-full bg-transparent text-[14px] text-ink placeholder:text-ink-soft focus:outline-none',
            className
          )}
          {...rest}
        />
        {trailing && <span className="text-ink-mute">{trailing}</span>}
      </div>
      {(hint || error) && (
        <p className={cn('text-[12px]', error ? 'text-danger' : 'text-ink-mute')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
